import type { GameObj } from 'kaplay'
import type { KAPLAYCtx, FloorSceneOpts } from '../types.ts'
import loadSoundsAssets from '../assets/sounds.ts'
import loadSpriteAssets from '../assets/sprites.ts'
import { useRooms } from '../rooms/index.ts'
import { useFloors } from '../floors/index.ts'
import { useGameUiControls } from '../objects/game-ui-controls.ts'
import { useElevatorPopup } from '../objects/elevator-popup.ts'
import { usePlayer } from '../characters/player.ts'
import { getTiledSize } from '../utils.ts'
import { usePcEasterEggPopup } from '../objects/easteregg-pc.ts'

export function useFloor12Scene(k: KAPLAYCtx) {
  loadSoundsAssets(k)
  loadSpriteAssets(k)

  const {
    createGameUiControls,
    resizeGameUiControls,
    // destroyGameUiControls,
  } = useGameUiControls(k)

  const {
    showElevatorPopup,
    hideElevatorPopup,
  } = useElevatorPopup(k)

  const { showPcEasterEggPopup } = usePcEasterEggPopup(k)

  let floor12: GameObj
  const { setFloor12 } = useFloors(k)

  k.scene('floor12', (opts?: FloorSceneOpts) => {
    let enterByStairsElevator = !!opts?.playerPos

    opts = opts || {}

    const {
      setFocusRoomA,
      setTreadmillRoom,
      setWC,
      setWCHandicap,
      setWCFemale,
      setWCMale,
      setMeetingRoomG,
      setMeetingRoomI,
      setMeetingRoomH,
      setMeetingRoomF,
      setMeetingRoomE,
    } = useRooms(k)

    const { setPlayer } = usePlayer(k)
    const player = opts?.playerPos
      ? setPlayer(k.vec2(opts.playerPos.x, opts.playerPos.y))
      : setPlayer(k.vec2(getTiledSize(25), getTiledSize(48)))

    // Camera follows player
    player.onUpdate(() => k.setCamPos(player.pos))

    floor12 = setFloor12()

    k.onCollide('floor_12_door','player', (door: GameObj) => {
      k.flash(k.BLACK, 0.5)
      floor12.destroy()
      k.play('sfx_door', { volume: 0.2 })

      if (door.tags.includes('floor_12_door_to_wc')) {
        setWC({
          onExit: () => {
            player.setPos(k.vec2(getTiledSize(15.5), getTiledSize(34)))
            floor12 = setFloor12()
          },
        })
      } else if (door.tags.includes('floor_12_door_to_meeting_room_e')) {
        setMeetingRoomE({
          onExit: () => {
            player.setPos(k.vec2(getTiledSize(31), getTiledSize(33)))
            floor12 = setFloor12()
          },
        })
      } else if (door.tags.includes('floor_12_door_to_focus_room')) {
        setFocusRoomA({
          onExit: () => {
            player.setPos(k.vec2(getTiledSize(40), getTiledSize(29)))
            floor12 = setFloor12()
          },
        })
      } else if (door.tags.includes('floor_12_door_to_treadmill_room')) {
        setTreadmillRoom({
          onExit: () => {
            player.setPos(k.vec2(getTiledSize(64.5), getTiledSize(32)))
            floor12 = setFloor12()
          },
        })
      } else if (door.tags.includes('floor_12_door_to_wc_handicap')) {
        setWCHandicap({
          onExit: () => {
            player.setPos(k.vec2(getTiledSize(67), getTiledSize(39)))
            floor12 = setFloor12()
          },
        })
      } else if (door.tags.includes('floor_12_door_to_wc_female')) {
        setWCFemale({
          onExit: () => {
            player.setPos(k.vec2(getTiledSize(69.5), getTiledSize(25)))
            floor12 = setFloor12()
          },
        })
      } else if (door.tags.includes('floor_12_door_to_wc_male')) {
        setWCMale({
          onExit: () => {
            player.setPos(k.vec2(getTiledSize(70.5), getTiledSize(28)))
            floor12 = setFloor12()
          },
        })
      } else if (door.tags.includes('floor_12_door_to_meeting_room_g')) {
        setMeetingRoomG({
          onExit: () => {
            player.setPos(k.vec2(getTiledSize(43), getTiledSize(30)))
            floor12 = setFloor12()
          },
        })
      } else if (door.tags.includes('floor_12_door_to_meeting_room_h')) {
        setMeetingRoomH({
          onExit: () => {
            player.setPos(k.vec2(getTiledSize(43), getTiledSize(33)))
            floor12 = setFloor12()
          },
        })
      } else if (door.tags.includes('floor_12_door_to_meeting_room_i')) {
        setMeetingRoomI({
          onExit: () => {
            player.setPos(k.vec2(getTiledSize(49.5), getTiledSize(27)))
            floor12 = setFloor12()
          },
        })
      } else if (door.tags.includes('floor_12_door_to_meeting_room_f')) {
        setMeetingRoomF({
          onExit: () => {
            player.setPos(k.vec2(getTiledSize(64.5), getTiledSize(45)))
            floor12 = setFloor12()
          },
        })
      }
    })

    // Elevators
    k.onCollide('player', 'floor_12_elevator', () => {
      if(enterByStairsElevator) return
      showElevatorPopup()
    })
    k.onCollideEnd('player', 'floor_12_elevator', () => {
      if(enterByStairsElevator) return enterByStairsElevator = false
      hideElevatorPopup()
    })

    // Stairs
    k.onCollide('player', 'floor_12_stairs', () => {
      if(enterByStairsElevator) return
      showElevatorPopup({origin: 'stairs'})
    })
    k.onCollideEnd('player', 'floor_12_stairs', () => {
      if(enterByStairsElevator) return enterByStairsElevator = false
      hideElevatorPopup()
    })

    // SFX
    k.onCollide('player', 'door_exit', () => {
      k.play('sfx_door', { volume: 0.2 })
    })
    k.onCollide('player', 'colliders', () => {
      k.play('sfx_hurt', { volume: 0.1 })
    })
    k.onCollide('player', 'sfx_emit_action', () => {
      k.play('sfx_action', { volume: 0.5 })
    })

    k.onClick('floor_12_pc_easteregg', () => {
      showPcEasterEggPopup()
    })

    createGameUiControls()

    k.onLoad(()=>k.flash(k.BLACK, 1))
    k.onResize(() => resizeGameUiControls())

  })

}
